// @ts-nocheck
/**
 * Add Credits dialog — v1 logic preserved end-to-end (regex-clamped decimals,
 * multiplier options 1..10, demo-company exact-amount + recharge-cap + expired
 * fast-path rules, effective-credits validation vs. distributable balance,
 * "Unlimited" mode). Ported off the compat shim to shadcn Dialog/Input/Button.
 */
import { useEffect } from 'react';
import { Loader2, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const MULTIPLIER_OPTIONS = Array.from({ length: 10 }, (_, i) => i + 1);

const formatAmount = (amount) => {
  const n = Number(amount);
  if (!Number.isFinite(n)) return 0;
  return n % 1 === 0 ? n : n.toFixed(2);
};

const normalizeCreditInput = (value) => {
  if (value === '' || value === '.') return value;
  if (!/^\d+(\.\d{0,2})?$/.test(value)) return null;

  if (value.includes('.')) {
    const [intPart, decPart] = value.split('.');
    const normalizedInt = intPart.replace(/^0+(?=\d)/, '') || '0';
    return `${normalizedInt}.${decPart}`;
  }

  return value.replace(/^0+(?=\d)/, '') || '0';
};

const selectCls =
  'flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';

const cx = (...args) => args.filter(Boolean).join(' ');

function RenderAddCreditModal(props) {
  const handleOpenChange = (next) => {
    if (!next) {
      if (props.submitting) return;
      props.setShow(false);
    }
  };
  const handleClose = () => props.setShow(false);

  const availableCredits = Number(props.availableCredits) || 0;

  const isDemoTarget = props.isDemoTarget === true;
  const demoBalance = Number(props.demoBalance) || 0;
  const demoMaxAmount = Number(props.demoMaxAmount) || 0;
  const demoExactMode = isDemoTarget && demoMaxAmount > 0;
  const demoRechargeUsed = Number(props.demoRechargeUsed) || 0;
  const demoRechargeMax = Number(props.demoRechargeMax) || 0;
  const demoRechargeExhausted =
    isDemoTarget && demoRechargeMax > 0 && demoRechargeUsed >= demoRechargeMax;
  const demoExpired = isDemoTarget && props.demoExpired === true;
  const demoBlocked =
    isDemoTarget && demoBalance > 0 && !(demoExpired && !demoRechargeExhausted);

  useEffect(() => {
    if (demoExactMode && props.show && !demoBlocked && !demoRechargeExhausted) {
      if (Number(props.creditsValue) !== demoMaxAmount) {
        props.setCreditsValue(String(demoMaxAmount));
      }
    }
  }, [demoExactMode, props.show, demoBlocked, demoRechargeExhausted, demoMaxAmount]);

  const multiplierLocked = !isDemoTarget && props.multiplierLocked === true;
  const showMultiplier = !isDemoTarget && !multiplierLocked && props.showMultiplier !== false;
  const multiplier = Number(props.multiplier) || 1;
  const baseAmount = parseFloat(props.creditsValue) || 0;
  const effectiveCredits = baseAmount * multiplier;
  const isUnlimited = props.isUnlimitedCredits === true && !isDemoTarget;
  const usesMultiplier = showMultiplier || multiplierLocked;

  const handleCreditChange = (e) => {
    const value = e.target.value;
    if (value === '') {
      props.setCreditsValue('');
      return;
    }
    const normalized = normalizeCreditInput(value);
    if (normalized != null) {
      if (isDemoTarget && demoMaxAmount > 0 && parseFloat(normalized) > demoMaxAmount) {
        props.setCreditsValue(String(demoMaxAmount));
        return;
      }
      props.setCreditsValue(normalized);
    }
  };

  const handleStep = () => {
    if (demoBlocked || demoExactMode || demoRechargeExhausted) return;
    const currentValue = parseFloat(props.creditsValue) || 0;
    const next = currentValue + 1;
    if (isDemoTarget && demoMaxAmount > 0 && next > demoMaxAmount) {
      props.setCreditsValue(String(demoMaxAmount));
      return;
    }
    props.setCreditsValue(String(next));
  };

  const effectiveOver =
    usesMultiplier && !isUnlimited && baseAmount > 0 && effectiveCredits > availableCredits;
  const showDemoExpiredNote = demoExpired && !demoRechargeExhausted && demoBalance > 0;
  const hasSubjectBalance = props.subjectName != null && props.subjectBalance != null;
  const showBalanceBox = hasSubjectBalance || demoBlocked || showDemoExpiredNote;

  const confirmDisabled =
    props.submitting ||
    demoBlocked ||
    demoRechargeExhausted ||
    (demoExactMode && baseAmount !== demoMaxAmount) ||
    effectiveOver;

  const creditKind = String(props.subjectBalanceLabel || 'credits')
    .replace(/^Available\s+/i, '')
    .trim();

  return (
    <Dialog open={!!props.show} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{props?.title || 'Add Voice Credits'}</DialogTitle>
        </DialogHeader>

        {/* Selected company's available credits only. */}
        {showBalanceBox && (
          <div className="rounded-md border border-sky-200 bg-sky-50 p-3 dark:border-sky-900/40 dark:bg-sky-900/20">
            {hasSubjectBalance && (
              <>
                <div className="text-sm font-medium text-sky-700 dark:text-sky-300">
                  {props.subjectBalanceLabel || 'Available credits'} ({props.subjectName})
                </div>
                <div className="text-xl font-semibold text-sky-900 dark:text-sky-100">
                  {formatAmount(props.subjectBalance)}
                </div>
              </>
            )}
            {demoBlocked && (
              <div
                className={cx(
                  'text-xs text-rose-600 dark:text-rose-400',
                  hasSubjectBalance && 'mt-1.5',
                )}
              >
                Demo company already has credits. You can add credits only when the balance is 0 or below.
              </div>
            )}
            {showDemoExpiredNote && (
              <div
                className={cx(
                  'text-xs text-rose-600 dark:text-rose-400',
                  hasSubjectBalance && 'mt-1.5',
                )}
              >
                This demo company has expired. Recharging is allowed even with credits left; the balance is
                reset to {formatAmount(demoMaxAmount || baseAmount)} and the expiry is extended.
              </div>
            )}
          </div>
        )}

        {/* Amount to add */}
        <div className="space-y-2">
          <label htmlFor="creditValue" className="text-sm font-medium">
            Amount to Add:
          </label>
          <div className="flex">
            <Input
              id="creditValue"
              type="text"
              inputMode="decimal"
              value={props.creditsValue === 0 ? '' : props.creditsValue}
              onChange={handleCreditChange}
              onFocus={(e) => {
                if (e.target.value === '0') props.setCreditsValue('');
              }}
              placeholder="0"
              disabled={demoBlocked || demoExactMode || demoRechargeExhausted}
              className="rounded-r-none focus-visible:ring-primary"
            />
            <Button
              type="button"
              onClick={handleStep}
              disabled={demoBlocked || demoExactMode || demoRechargeExhausted}
              className="rounded-l-none px-3"
              aria-label="Increase by 1"
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          {demoExactMode && !demoBlocked && !demoRechargeExhausted && (
            <div className="text-xs text-sky-700 dark:text-sky-300">
              A demo company can only be credited with exactly {formatAmount(demoMaxAmount)}. This is the
              fixed amount set by the super admin — you can add this amount or skip adding credits.
              {demoRechargeMax > 0 && (
                <>
                  {' '}
                  This demo company has {Math.max(demoRechargeMax - demoRechargeUsed, 0)} of{' '}
                  {demoRechargeMax} recharge(s) remaining.
                </>
              )}
            </div>
          )}
          {demoRechargeExhausted && (
            <div className="text-xs text-rose-600 dark:text-rose-400">
              This demo company has reached its maximum of {demoRechargeMax} recharge(s) and cannot be
              credited again.
            </div>
          )}
          {effectiveOver && (
            <p className="text-sm text-rose-600 dark:text-rose-400">
              Your distributable {creditKind} balance ({formatAmount(availableCredits)}) is lower than the{' '}
              {formatAmount(effectiveCredits)} required ({formatAmount(baseAmount)} × {multiplier}x).
            </p>
          )}
        </div>

        {/* Multiplier — can be chosen only on the first grant */}
        {showMultiplier && (
          <div className="space-y-2">
            <label htmlFor="creditMultiplier" className="text-sm font-medium">
              Multiplier:
            </label>
            <select
              id="creditMultiplier"
              className={selectCls}
              value={props.multiplier}
              onChange={(e) => props.setMultiplier(Number(e.target.value))}
            >
              {MULTIPLIER_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}x
                </option>
              ))}
            </select>
            <p className="text-xs text-amber-700 dark:text-amber-300">
              The multiplier can be set only once, on the first grant.
            </p>
          </div>
        )}
        {multiplierLocked && (
          <div className="space-y-1 rounded-md border border-amber-200 bg-amber-50 p-3 dark:border-amber-900/40 dark:bg-amber-900/20">
            <div className="text-sm font-medium text-amber-800 dark:text-amber-200">
              Multiplier: {multiplier}x
            </div>
            <p className="text-xs text-amber-700 dark:text-amber-300">
              The multiplier can be set only once, on the first grant. This company already has {multiplier}x, and this grant uses that same multiplier.
            </p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={props.submitting}>
            Close
          </Button>
          <Button
            onClick={() => {
              if (props.submitting) return;
              props.setConfirmUpdateCredit(true);
            }}
            disabled={confirmDisabled || props.submitting}
          >
            {props.submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            {props.submitting ? 'Processing...' : 'Confirm'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default RenderAddCreditModal;
